// biome-ignore-all lint/a11y: TODO - address a11y issues later
import { EllipsisIcon } from "lucide-react";
import * as React from "react";
import { cn } from "@/lib/utils";
import { Card } from "./card";

interface ResizableCardProps extends React.HTMLAttributes<HTMLDivElement> {
  defaultHeight?: number;
  minHeight?: number;
  maxHeight?: number;
  onHeightChange?: (height: number) => void;
  roundedTop?: boolean;
  roundedBottom?: boolean;
}

export const ResizableCard = React.forwardRef<
  HTMLDivElement,
  ResizableCardProps
>(
  (
    {
      className,
      defaultHeight = 250,
      minHeight = 150,
      maxHeight = 600,
      onHeightChange,
      roundedTop = true,
      roundedBottom = true,
      children,
      ...props
    },
    ref,
  ) => {
    const [height, setHeight] = React.useState(defaultHeight);
    const [isResizing, setIsResizing] = React.useState(false);
    const startY = React.useRef<number | null>(null);
    const startHeight = React.useRef<number>(defaultHeight);
    const cardRef = React.useRef<HTMLDivElement>(null);

    // Use the forwarded ref or the internal ref
    const resolvedRef = ref || cardRef;

    const handleMouseDown = (e: React.MouseEvent) => {
      e.preventDefault();
      startY.current = e.clientY;
      startHeight.current = height;
      setIsResizing(true);

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    };

    const handleMouseMove = React.useCallback(
      (e: MouseEvent) => {
        if (startY.current === null) return;

        // Prevent default browser behavior to avoid text selection or other side effects
        e.preventDefault();

        // Calculate only vertical change
        const deltaY = e.clientY - startY.current;
        const newHeight = Math.max(
          minHeight,
          Math.min(maxHeight, startHeight.current + deltaY),
        );

        // Apply height change without affecting width
        setHeight(newHeight);
        onHeightChange?.(newHeight);
      },
      [minHeight, maxHeight, onHeightChange],
    );

    const handleMouseUp = React.useCallback(() => {
      startY.current = null;
      setIsResizing(false);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    }, [handleMouseMove]);

    React.useEffect(() => {
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }, [handleMouseMove, handleMouseUp]);

    // Define the resize handle height (in px) - keep in sync with the h-2 class
    const handleHeight = 8; // 2rem = 8px in most Tailwind configs

    return (
      <Card
        ref={resolvedRef}
        className={cn(
          "relative w-full box-border min-w-full max-w-full",
          // Only apply overflow-hidden if no overflow-visible class is provided
          !className?.includes("overflow-visible") ? "overflow-hidden" : "",
          roundedTop ? "" : "rounded-t-none",
          roundedBottom ? "" : "rounded-b-none",
          isResizing ? "" : "transition-[height] duration-100 ease-out",
          className,
        )}
        style={{
          height: `${height}px`,
        }}
        {...props}
      >
        {/* Content wrapper that excludes space for the handle */}
        <div
          className={cn(
            "w-full flex flex-col",
            // Only apply overflow-auto if no overflow-visible class is provided in parent
            !className?.includes("overflow-visible")
              ? "overflow-auto"
              : "overflow-visible",
          )}
          style={{
            height: `${height - handleHeight}px`,
          }}
        >
          {children}
        </div>

        {/* Resize handle */}
        <div
          className="absolute bottom-0 left-0 right-0 flex h-2 cursor-ns-resize items-center justify-center bg-muted hover:bg-primary/20 border-t transition-colors duration-200"
          onMouseDown={handleMouseDown}
          title="Drag to resize"
        >
          <div className="flex items-center justify-center rounded-sm transition-colors duration-200">
            <EllipsisIcon className="h-5 w-5 text-muted-foreground/40 hover:text-muted-foreground/60" />
          </div>
        </div>
      </Card>
    );
  },
);

ResizableCard.displayName = "ResizableCard";
